import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export interface AccessGateDecision {
  mode: 'login' | 'waitlist';
  availableSlots: number;
  enforced: boolean;
}

/**
 * Where the landing page should send someone who has no account.
 *
 * The answer defaults to signup and stays there whenever it is unknown —
 * before the request resolves, and after it fails. Two reasons: showing a
 * waitlist and then flipping it to signup is worse than starting open, and
 * this is marketing copy routing rather than an authorisation decision. The
 * backend re-checks capacity at the moment a slot is actually spent, so a
 * visitor let through here is not let through anywhere that matters.
 */
export function useAccessGate() {
  const query = useQuery<AccessGateDecision>({
    queryKey: ['access-gate'],
    queryFn: async () => {
      const { data } = await api.get('/access-gate');
      return data;
    },
    staleTime: 60_000,
    retry: false,
  });

  return {
    destination: query.data?.mode === 'waitlist' ? ('/waitlist' as const) : ('/login' as const),
    isWaitlist: query.data?.mode === 'waitlist',
    isLoading: query.isLoading,
  };
}
